<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-[1280px] mx-auto px-4 space-y-8">
      
      <!-- Welcome Header -->
      <div class="mb-8">
        <h1 class="sci-page-title-only">Painel de Controle (Master)</h1>
        <p class="text-slate-500 font-medium mt-1">Visão global do sistema referente a {{ currentMonthName }} de {{ currentYear }}</p>
      </div>

      <!-- Prime KPIs (Finance and Volume) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Investimento Total -->
        <div class="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 shadow-2xl shadow-slate-900/10 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-slate-900/20">
          <div class="absolute -right-10 -top-10 opacity-5">
            <v-icon size="180" color="white">mdi-currency-brl</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[160px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/10 p-2.5 rounded-xl border border-white/5">
                <v-icon color="white" small>mdi-cash-multiple</v-icon>
              </div>
              <h3 class="text-white/80 font-bold uppercase tracking-widest text-xs">INVESTIMENTO GLOBAL (MÊS)</h3>
            </div>
            <div>
              <p class="text-4xl lg:text-5xl font-black text-white tracking-tighter">
                R$ <span class="font-mono">{{ data.totalValueMonth ? data.totalValueMonth.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00' }}</span>
              </p>
              <p class="text-emerald-400 font-bold text-xs mt-3 flex items-center gap-1 bg-emerald-400/10 w-fit px-3 py-1 rounded-full border border-emerald-400/20">
                <v-icon small color="emerald" class="mr-1">mdi-trending-up</v-icon> Somatório total de todas as cidades neste mês
              </p>
            </div>
          </div>
        </div>

        <!-- Volume Total -->
        <div class="bg-gradient-to-br from-orange-500 to-[#FF4600] rounded-3xl p-8 shadow-2xl shadow-orange-500/20 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-orange-500/30">
          <div class="absolute -right-6 -bottom-6 opacity-[0.08] transform rotate-12">
            <v-icon size="160" color="white">mdi-gas-station</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[160px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/20 p-2.5 rounded-xl border border-white/10">
                <v-icon color="white" small>mdi-water-outline</v-icon>
              </div>
              <h3 class="text-white/90 font-bold uppercase tracking-widest text-xs tracking-widest">VOLUME ABASTECIDO (MÊS)</h3>
            </div>
            <div>
              <p class="text-4xl lg:text-5xl font-black text-white tracking-tighter">
                <span class="font-mono">{{ data.totalLitersMonth ? data.totalLitersMonth.toLocaleString('pt-BR', { minimumFractionDigits: 1 }) : '0,0' }}</span> <span class="text-3xl lg:text-4xl text-white/80 font-bold ml-1">L</span>
              </p>
              <p class="text-white font-bold text-xs mt-3 bg-white/20 w-fit px-3 py-1 rounded-full border border-white/10">
                Consumo somado em toda a plataforma
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Secondary KPIs -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Card 1 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-blue-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-blue-100">
            <v-icon color="blue" size="28">mdi-city-variant-outline</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Cidades Ativas</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.prefectures || 0 }}</p>
          </div>
        </div>
        
        <!-- Card 2 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-emerald-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-emerald-100">
            <v-icon color="success" size="28">mdi-car-multiple</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Frota Total</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.vehicles || 0 }}</p>
          </div>
        </div>

        <!-- Card 3 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-orange-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-orange-100">
            <v-icon color="orange flex" size="28">mdi-card-account-details-outline</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Motoristas</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.drivers || 0 }}</p>
          </div>
        </div>

        <!-- Card 4 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-purple-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-purple-100">
            <v-icon color="purple" size="28">mdi-store-outline</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Postos Credenciados</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.providers || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Charts Data -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <!-- Consumo Graph -->
        <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
          <div class="flex items-center justify-between mb-8">
            <h3 class="text-sm font-black text-slate-800 uppercase tracking-widest">Ranking de Consumo por Cidade</h3>
            <span class="bg-slate-100 text-slate-500 text-[10px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-widest">{{ currentMonthName }}</span>
          </div>
          <div style="height: 350px;" class="w-full">
            <TechBarChart 
              v-if="monthLabels.length > 0"
              :chartData="monthValues" 
              :labels="monthLabels" 
            />
            <div v-else class="h-full flex flex-col items-center justify-center text-slate-400">
               <v-icon size="64" color="slate-200" class="mb-4">mdi-chart-bar-stacked</v-icon>
               <span class="text-[11px] font-bold uppercase tracking-widest text-slate-400">Sem Dados Registrados no Mês</span>
            </div>
          </div>
        </div>

        <!-- Tendencia Graph -->
        <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
          <div class="flex items-center justify-between mb-8">
            <h3 class="text-sm font-black text-slate-800 uppercase tracking-widest">Tendência Histórica de Abastecimentos</h3>
            <span class="bg-orange-100 text-[#FF4600] text-[10px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-widest">{{ currentYear }}</span>
          </div>
          <div style="height: 350px;" class="w-full">
            <TechLineChart 
              v-if="yearLabels.length > 0"
              :chartData="yearValues" 
              :labels="yearLabels"
            />
             <div v-else class="h-full flex flex-col items-center justify-center text-slate-400">
               <v-icon size="64" color="slate-200" class="mb-4">mdi-chart-line-variant</v-icon>
               <span class="text-[11px] font-bold uppercase tracking-widest text-slate-400">Sem Dados Registrados no Ano</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Helpers from "../../mixins/helpers";
import TechBarChart from "./components/TechBarChart";
import TechLineChart from "./components/TechLineChart";

export default {
  components: {
    TechBarChart,
    TechLineChart
  },
  props: ["prefecture_id"],
  mixins: [Helpers],
  created() {
    this.initialize();
  },
  data: () => ({
    data: {
      prefectures: 0,
      providers: 0,
      vehicles: 0,
      drivers: 0,
      totalLitersMonth: 0,
      totalValueMonth: 0,
      newSuppliesMonth: [],
      newSuppliesYear: [],
      targetMonth: null,
      targetYear: null,
    },
  }),
  computed: {
    monthLabels() {
      return this.data.newSuppliesMonth ? this.data.newSuppliesMonth.map(item => item[0]) : [];
    },
    monthValues() {
      return this.data.newSuppliesMonth ? this.data.newSuppliesMonth.map(item => item[1]) : [];
    },
    yearLabels() {
      return this.data.newSuppliesYear ? this.data.newSuppliesYear.map(item => item[0]) : [];
    },
    yearValues() {
      return this.data.newSuppliesYear ? this.data.newSuppliesYear.map(item => item[1]) : [];
    },
    currentMonthName() {
      const months = [
        "JANEIRO", "FEVEREIRO", "MARÇO", "ABRIL", "MAIO", "JUNHO",
        "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO"
      ];
      const monthIndex = this.data.targetMonth ? this.data.targetMonth - 1 : new Date().getMonth();
      return months[monthIndex];
    },
    currentYear() {
      return this.data.targetYear || new Date().getFullYear();
    }
  },
  methods: {
    initialize() {
      if (this.prefecture_id) {
        this.$http(`/dashboard/prefectures/${this.prefecture_id}`).then(
          (r) => (this.data = Object.assign({}, this.data, r.data))
        );
      } else {
        this.$http(`/dashboard`).then((r) => (this.data = Object.assign({}, this.data, r.data)));
      }
    },
  },
};
</script>

<style scoped>
</style>